import type {
  AIPipelineEdge,
  AIPipelineMetadata,
  AIPipelineNode,
  GeneratedAIPipeline,
  PipelineOption,
} from '@/types/ai-pipeline';
import type {
  PipelineEdge,
  PipelineNode,
  PipelineNodeData,
  PipelineNodeType,
  AggregateFunction,
  TransformationType,
  FilterOperator,
} from '@/pages/pipelines/types/pipeline';

const SUPPORTED_NODE_TYPES = new Set<PipelineNodeType>([
  'SOURCE',
  'TRANSFORMATION',
  'FILTER',
  'AGGREGATION',
  'JOIN',
  'UNION',
  'DESTINATION',
]);

const NODE_TYPE_ALIASES: Record<string, PipelineNodeType | 'PROJECTION'> = {
  PROJECTION: 'PROJECTION',
  SOURCE: 'SOURCE',
  EXTRACT: 'SOURCE',
  DATASOURCE: 'SOURCE',
  DATA_SOURCE: 'SOURCE',
  INPUT: 'SOURCE',
  INGEST: 'SOURCE',
  FILTER: 'FILTER',
  WHERE: 'FILTER',
  CONDITION: 'FILTER',
  AGGREGATION: 'AGGREGATION',
  AGGREGATE: 'AGGREGATION',
  GROUPBY: 'AGGREGATION',
  GROUP_BY: 'AGGREGATION',
  SUMMARY: 'AGGREGATION',
  JOIN: 'JOIN',
  LOOKUP: 'JOIN',
  UNION: 'UNION',
  CONCAT: 'UNION',
  TRANSFORMATION: 'TRANSFORMATION',
  TRANSFORM: 'TRANSFORMATION',
  MAP: 'TRANSFORMATION',
  CLEANSE: 'TRANSFORMATION',
  DESTINATION: 'DESTINATION',
  LOAD: 'DESTINATION',
  SINK: 'DESTINATION',
  OUTPUT: 'DESTINATION',
  TARGET: 'DESTINATION',
};

type FilterConditionNormalized = {
  field: string;
  operator: FilterOperator;
  value: unknown;
};

type RecommendedNodeSpec = {
  rawType: string;
  type: PipelineNodeType;
  label: string;
};

export interface NormalizeGeneratedPipelineOptions {
  selectedOption?: PipelineOption | null;
  contextDataSources?: Array<{
    id: string;
    schema: Record<string, string>;
  }>;
}

function toRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function toRecordLoose(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        return toRecord(parsed);
      } catch {
        return {};
      }
    }
  }
  return {};
}

function mergeRecordCandidates(...values: unknown[]): Record<string, unknown> {
  return values.reduce<Record<string, unknown>>((acc, value) => {
    const record = toRecordLoose(value);
    if (Object.keys(record).length > 0) {
      Object.assign(acc, record);
    }
    return acc;
  }, {});
}

function toString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
}

function toBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
    if (normalized === 'false' || normalized === '0' || normalized === 'no') return false;
  }
  return undefined;
}

function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(toString).filter((item) => item.length > 0);
  }
  const single = toString(value);
  return single
    ? single
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
    : [];
}

function pickString(...values: unknown[]): string {
  for (const value of values) {
    const text = toString(value);
    if (text.length > 0) return text;
  }
  return '';
}

function pickNumber(...values: unknown[]): number | undefined {
  for (const value of values) {
    const num = toNumber(value);
    if (typeof num === 'number') return num;
  }
  return undefined;
}

function pickBoolean(...values: unknown[]): boolean | undefined {
  for (const value of values) {
    const flag = toBoolean(value);
    if (typeof flag === 'boolean') return flag;
  }
  return undefined;
}

function pickStringArray(...values: unknown[]): string[] {
  for (const value of values) {
    const arr = toStringArray(value);
    if (arr.length > 0) return arr;
  }
  return [];
}

function normalizeFilterOperator(value: unknown): FilterOperator {
  const operator = toString(value).toLowerCase().replace(/\s+/g, '_');
  const map: Record<string, FilterOperator> = {
    '=': 'eq',
    '==': 'eq',
    eq: 'eq',
    equals: 'eq',
    '!=': 'neq',
    '<>': 'neq',
    neq: 'neq',
    not_equals: 'neq',
    '>': 'gt',
    gt: 'gt',
    '>=': 'gte',
    gte: 'gte',
    '<': 'lt',
    lt: 'lt',
    '<=': 'lte',
    lte: 'lte',
    in: 'in',
    not_in: 'nin',
    nin: 'nin',
    like: 'like',
    contains: 'contains',
  };
  return map[operator] || 'eq';
}

function stripWrappingQuotes(value: string): string {
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    return value.slice(1, -1).trim();
  }
  return value;
}

function normalizeFilterValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.join(',');
  }
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }
  if (value === null || value === undefined) {
    return '';
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function parseConditionExpression(expression: string): FilterConditionNormalized | null {
  const cleaned = expression.trim().replace(/^\(+|\)+$/g, '');
  if (!cleaned) return null;

  const match = cleaned.match(
    /^([a-zA-Z_][\w.]*)\s*(=|==|!=|<>|>=|<=|>|<|IN|NOT\s+IN|LIKE|CONTAINS)\s*(.+)$/i,
  );
  if (!match) return null;

  const [, rawField, rawOperator, rawValue] = match;
  const operator = normalizeFilterOperator(rawOperator);
  const valueText = rawValue.trim().replace(/;$/, '');

  if (operator === 'in' || operator === 'nin') {
    const inner = valueText.replace(/^\(/, '').replace(/\)$/, '');
    const values = inner
      .split(',')
      .map((item) => stripWrappingQuotes(item.trim()))
      .filter((item) => item.length > 0);
    return {
      field: rawField,
      operator,
      value: values.join(','),
    };
  }

  const scalar = stripWrappingQuotes(valueText);
  const numericValue = toNumber(scalar);
  const boolValue = toBoolean(scalar);

  return {
    field: rawField,
    operator,
    value:
      typeof numericValue === 'number'
        ? numericValue
        : typeof boolValue === 'boolean'
          ? boolValue
          : scalar,
  };
}

function parseConditionsFromExpression(expression: string): {
  conditions: FilterConditionNormalized[];
  detectedLogicalOperator?: 'AND' | 'OR';
} {
  const trimmed = expression.trim();
  if (!trimmed) return { conditions: [] };

  const parts = trimmed.split(/\s+(AND|OR)\s+/i);
  if (parts.length === 1) {
    const single = parseConditionExpression(trimmed);
    return {
      conditions: single ? [single] : [],
    };
  }

  let detectedLogicalOperator: 'AND' | 'OR' | undefined;
  const conditions: FilterConditionNormalized[] = [];

  for (let i = 0; i < parts.length; i += 2) {
    const conditionExpr = parts[i];
    const parsed = parseConditionExpression(conditionExpr);
    if (parsed) conditions.push(parsed);

    const logical = toString(parts[i + 1]).toUpperCase();
    if (!detectedLogicalOperator && (logical === 'AND' || logical === 'OR')) {
      detectedLogicalOperator = logical;
    }
  }

  return { conditions, detectedLogicalOperator };
}

function normalizeFilterConditions(config: Record<string, unknown>): {
  conditions: FilterConditionNormalized[];
  detectedLogicalOperator?: 'AND' | 'OR';
} {
  const conditionCandidates = [
    config.conditions,
    config.filters,
    config.predicates,
    config.rules,
    config.rule,
  ];

  const rawConditions = conditionCandidates.find((item) => Array.isArray(item));
  const conditions = (Array.isArray(rawConditions) ? rawConditions : [])
    .map((item) => {
      const condition = toRecordLoose(item);
      if (Object.keys(condition).length === 0 && typeof item === 'string') {
        return parseConditionExpression(item);
      }

      const expressionText = pickString(condition.expression, condition.where, condition.sql);
      if (expressionText) {
        return parseConditionExpression(expressionText);
      }

      const field = pickString(
        condition.field,
        condition.column,
        condition.key,
        condition.fieldName,
        condition.name,
      );
      if (!field) return null;
      return {
        field,
        operator: normalizeFilterOperator(condition.operator || condition.op || condition.comparison),
        value: normalizeFilterValue(
          condition.value ?? condition.values ?? condition.term ?? condition.expected,
        ),
      };
    })
    .filter((item): item is FilterConditionNormalized => !!item);

  if (conditions.length > 0) {
    return { conditions };
  }

  const singleField = pickString(config.field, config.column, config.fieldName);
  if (singleField) {
    return {
      conditions: [
        {
          field: singleField,
          operator: normalizeFilterOperator(config.operator || config.op || config.comparison),
          value: normalizeFilterValue(config.value),
        },
      ],
    };
  }

  const expression = pickString(
    config.where,
    config.expression,
    config.filterCondition,
    config.condition,
    config.sql,
  );
  if (expression) {
    return parseConditionsFromExpression(expression);
  }

  return { conditions: [] };
}

function normalizeAggregateFunction(value: unknown): AggregateFunction {
  const fn = toString(value).toUpperCase();
  const map: Record<string, AggregateFunction> = {
    SUM: 'SUM',
    TOTAL: 'SUM',
    AVG: 'AVG',
    AVERAGE: 'AVG',
    MEAN: 'AVG',
    COUNT: 'COUNT',
    CNT: 'COUNT',
    MIN: 'MIN',
    MAX: 'MAX',
    DISTINCT_COUNT: 'DISTINCT_COUNT',
    COUNT_DISTINCT: 'DISTINCT_COUNT',
    NUNIQUE: 'DISTINCT_COUNT',
  };
  return map[fn] || 'COUNT';
}

function asAggregationArray(value: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(value)) {
    return value.map((item) => toRecordLoose(item)).filter((item) => Object.keys(item).length > 0);
  }

  const obj = toRecordLoose(value);
  if (Object.keys(obj).length > 0) {
    const items: Array<Record<string, unknown>> = [];
    Object.entries(obj).forEach(([alias, payload]) => {
      const details = toRecordLoose(payload);
      if (Object.keys(details).length === 0) return;
      items.push({
        alias,
        ...details,
      });
    });
    return items;
  }

  return [];
}

function normalizeAggregationRules(config: Record<string, unknown>): Array<{
  field: string;
  function: AggregateFunction;
  alias: string;
}> {
  const rawAggs = asAggregationArray(
    config.aggregations || config.metrics || config.measures || config.calculations,
  );

  const aggs = rawAggs
    .map((item) => {
      const agg = toRecordLoose(item);
      const field = pickString(agg.field, agg.column, agg.metric, agg.sourceField, agg.name);
      if (!field) return null;
      const fn = normalizeAggregateFunction(agg.function || agg.fn || agg.aggregation || agg.op || agg.type);
      const alias = pickString(agg.alias, agg.as, agg.outputField, agg.targetField) || `${fn.toLowerCase()}_${field}`;
      return { field, function: fn, alias };
    })
    .filter((item): item is { field: string; function: AggregateFunction; alias: string } => !!item);

  if (aggs.length === 0) {
    const field = pickString(config.field, config.metricField, config.valueField);
    if (field) {
      const fn = normalizeAggregateFunction(config.function || config.fn || config.operation);
      aggs.push({ field, function: fn, alias: `${fn.toLowerCase()}_${field}` });
    }
  }

  return aggs;
}

function normalizeTransformationType(value: unknown): TransformationType {
  const raw = toString(value).toUpperCase();
  const map: Record<string, TransformationType> = {
    RENAME: 'RENAME',
    CAST: 'CAST',
    SPLIT: 'SPLIT',
    MERGE: 'MERGE',
    JSON_RULES: 'JSON_RULES',
    JSONRULES: 'JSON_RULES',
    RULES: 'JSON_RULES',
    DECLARATIVE: 'JSON_RULES',
    JAVASCRIPT: 'JAVASCRIPT',
    JS: 'JAVASCRIPT',
    SCRIPT: 'JAVASCRIPT',
    CUSTOM: 'CUSTOM',
    MAP: 'CUSTOM',
    CALCULATE: 'CUSTOM',
    DERIVE: 'CUSTOM',
    PROJECTION: 'CUSTOM',
  };
  return map[raw] || 'CUSTOM';
}

function normalizeTransformationRuleConfig(
  transform: Record<string, unknown>,
  type: TransformationType,
): Record<string, unknown> {
  const provided = mergeRecordCandidates(transform.config, transform.params, transform.options);
  if (Object.keys(provided).length > 0) {
    return provided;
  }

  if (type === 'RENAME') {
    return {
      sourceField: pickString(transform.sourceField, transform.from, transform.oldName, transform.field),
      targetField: pickString(transform.targetField, transform.to, transform.newName),
    };
  }

  if (type === 'CAST') {
    return {
      field: pickString(transform.field, transform.sourceField),
      targetType: pickString(transform.targetType, transform.toType, transform.castTo),
    };
  }

  if (type === 'SPLIT') {
    return {
      field: pickString(transform.field, transform.sourceField),
      separator: pickString(transform.separator, transform.delimiter) || ',',
      targetFields: pickStringArray(transform.targetFields, transform.into),
    };
  }

  if (type === 'MERGE') {
    return {
      sourceFields: pickStringArray(transform.sourceFields, transform.fields),
      targetField: pickString(transform.targetField, transform.into),
      separator: pickString(transform.separator),
    };
  }

  if (type === 'JSON_RULES') {
    const rawRules = transform.rules ?? transform.jsonRules ?? transform.steps ?? transform.operations;
    return {
      rules: Array.isArray(rawRules) ? rawRules : toString(rawRules),
    };
  }

  if (type === 'JAVASCRIPT') {
    return {
      script: pickString(transform.script, transform.code, transform.expression),
      timeoutMs: pickNumber(transform.timeoutMs, transform.timeout),
    };
  }

  return {
    sourceField: pickString(transform.sourceField, transform.field),
    targetField: pickString(transform.targetField),
    expression: pickString(transform.expression, transform.formula, transform.sql),
    value: transform.value,
  };
}

function normalizeTransformations(
  config: Record<string, unknown>,
  rawNodeType: unknown,
): Array<{
  id: string;
  type: TransformationType;
  config: Record<string, unknown>;
}> {
  const rawType = toString(rawNodeType).toUpperCase();
  const operationHint = toString(config.operation || config.type || config.transformationType).toUpperCase();
  if (rawType === 'PROJECTION' || operationHint === 'PROJECTION') {
    return [
      {
        id: 'transform-projection-1',
        type: 'CUSTOM',
        config: {
          operation: 'PROJECTION',
          timeField: pickString(config.timeField, config.timeColumn),
          metricField: pickString(config.metricField, config.valueField),
          groupBy: pickStringArray(config.groupBy, config.group_by, config.dimensions),
          projectionYears: pickNumber(config.projectionYears, config.horizonYears, config.periods) || 1,
          method: pickString(config.method, config.model, config.algorithm) || 'linear',
        },
      },
    ];
  }

  const transformCandidates = [
    config.transformations,
    config.rules,
    config.operations,
    config.steps,
  ];
  let rawTransforms: Array<Record<string, unknown>> = [];
  for (const candidate of transformCandidates) {
    const parsed = asAggregationArray(candidate);
    if (parsed.length > 0) {
      rawTransforms = parsed;
      break;
    }
  }

  const transforms = rawTransforms
    .map((item, index) => {
      const transform = toRecordLoose(item);
      const type = normalizeTransformationType(
        transform.type || transform.operation || transform.op || transform.action || transform.kind,
      );
      const id = pickString(transform.id) || `transform-${index + 1}`;
      return {
        id,
        type,
        config: normalizeTransformationRuleConfig(transform, type),
      };
    })
    .filter((item) => item.id.length > 0);

  if (transforms.length > 0) return transforms;

  if (pickString(config.sourceField, config.from, config.field) || pickString(config.targetField, config.to)) {
    return [
      {
        id: 'transform-rename-1',
        type: 'RENAME',
        config: {
          sourceField: pickString(config.sourceField, config.from, config.field),
          targetField: pickString(config.targetField, config.to),
        },
      },
    ];
  }

  const customExpression = pickString(config.expression, config.formula, config.sql);
  if (customExpression) {
    return [
      {
        id: 'transform-custom-1',
        type: 'CUSTOM',
        config: {
          expression: customExpression,
        },
      },
    ];
  }

  return [];
}

function normalizeNodeType(rawType: unknown): PipelineNodeType {
  const normalized = toString(rawType).toUpperCase().replace(/\s+/g, '_');
  const mapped = NODE_TYPE_ALIASES[normalized];

  if (mapped === 'PROJECTION') {
    return 'TRANSFORMATION';
  }

  if (mapped && SUPPORTED_NODE_TYPES.has(mapped)) {
    return mapped;
  }

  if (SUPPORTED_NODE_TYPES.has(normalized as PipelineNodeType)) {
    return normalized as PipelineNodeType;
  }

  return 'TRANSFORMATION';
}

function normalizeWriteMode(value: unknown): 'APPEND' | 'OVERWRITE' | 'UPSERT' {
  const mode = toString(value).toUpperCase();
  if (mode === 'APPEND' || mode === 'OVERWRITE' || mode === 'UPSERT') {
    return mode;
  }
  return 'UPSERT';
}

type CastTargetType = 'NUMBER' | 'BOOLEAN' | 'DATE';
type CastRule = {
  id: string;
  type: 'CAST';
  config: {
    field: string;
    targetType: CastTargetType;
  };
};

function mapSchemaTypeToCastTarget(schemaType: unknown): CastTargetType | null {
  const normalized = toString(schemaType).toLowerCase();
  if (!normalized) return null;

  if (
    /(^|[^a-z])(int|int\d+|integer|smallint|bigint|tinyint|number|numeric|decimal|float|double|real|money)([^a-z]|$)/.test(
      normalized,
    )
  ) {
    return 'NUMBER';
  }

  if (/(^|[^a-z])(bool|boolean|bit)([^a-z]|$)/.test(normalized)) {
    return 'BOOLEAN';
  }

  if (/(^|[^a-z])(date|datetime|timestamp|time)([^a-z]|$)/.test(normalized)) {
    return 'DATE';
  }

  return null;
}

function buildCastRulesFromSchema(schema: Record<string, string>): CastRule[] {
  const rules: CastRule[] = [];
  const seenFields = new Set<string>();

  Object.entries(schema).forEach(([rawField, rawType], index) => {
    const field = toString(rawField);
    if (!field) return;
    const targetType = mapSchemaTypeToCastTarget(rawType);
    if (!targetType) return;

    const fieldKey = field.toLowerCase();
    if (seenFields.has(fieldKey)) return;
    seenFields.add(fieldKey);

    rules.push({
      id: `cast-normalize-${index + 1}`,
      type: 'CAST',
      config: {
        field,
        targetType,
      },
    });
  });

  return rules;
}

function normalizeSchemaRecord(value: unknown): Record<string, string> {
  const schema = toRecordLoose(value);
  const normalized: Record<string, string> = {};

  Object.entries(schema).forEach(([rawField, rawType]) => {
    const field = toString(rawField);
    const type = toString(rawType);
    if (!field || !type) return;
    normalized[field] = type;
  });

  return normalized;
}

function buildContextSchemaLookup(
  contextDataSources: NormalizeGeneratedPipelineOptions['contextDataSources'],
): Map<string, Record<string, string>> {
  const lookup = new Map<string, Record<string, string>>();
  if (!Array.isArray(contextDataSources)) return lookup;

  contextDataSources.forEach((source) => {
    const id = pickString(source?.id);
    const schema = normalizeSchemaRecord(source?.schema);
    if (!id || Object.keys(schema).length === 0) return;
    lookup.set(id, schema);
  });

  return lookup;
}

function normalizeTransformList(value: unknown): Array<{
  id: string;
  type: TransformationType;
  config: Record<string, unknown>;
}> {
  const list = Array.isArray(value) ? value : [];
  return list
    .map((item, index) => {
      const transform = toRecordLoose(item);
      const type = normalizeTransformationType(
        transform.type || transform.operation || transform.op || transform.action || transform.kind,
      );
      return {
        id: pickString(transform.id) || `transform-${index + 1}`,
        type,
        config: normalizeTransformationRuleConfig(transform, type),
      };
    })
    .filter((item) => item.id.length > 0);
}

function mergeCastRulesIntoTransformationNode(
  node: PipelineNode,
  castRules: CastRule[],
): { node: PipelineNode; added: number } {
  const currentConfig = toRecordLoose(node.data.config);
  const currentTransforms = normalizeTransformList(currentConfig.transformations);
  const seenIds = new Set(currentTransforms.map((item) => item.id));
  const existingCastKeys = new Set(
    currentTransforms
      .filter((item) => item.type === 'CAST')
      .map((item) => {
        const config = toRecordLoose(item.config);
        return `${pickString(config.field).toLowerCase()}::${pickString(config.targetType).toUpperCase()}`;
      }),
  );

  let added = 0;
  castRules.forEach((rule, index) => {
    const castKey = `${rule.config.field.toLowerCase()}::${rule.config.targetType}`;
    if (existingCastKeys.has(castKey)) return;

    let id = rule.id;
    while (seenIds.has(id)) {
      id = `${rule.id}-${index + added + 1}`;
    }
    seenIds.add(id);
    existingCastKeys.add(castKey);
    currentTransforms.push({
      id,
      type: 'CAST',
      config: {
        ...rule.config,
      },
    });
    added += 1;
  });

  if (added === 0) {
    return { node, added: 0 };
  }

  return {
    node: {
      ...node,
      data: {
        ...node.data,
        type: 'TRANSFORMATION',
        config: {
          ...currentConfig,
          type: 'TRANSFORMATION',
          transformations: currentTransforms,
        },
      },
    },
    added,
  };
}

function ensureTypeNormalizationLayer(
  nodes: PipelineNode[],
  inputEdges: PipelineEdge[],
  options: {
    contextDataSources?: NormalizeGeneratedPipelineOptions['contextDataSources'];
    defaultSourceId: string;
  },
): { nodes: PipelineNode[]; edges: PipelineEdge[] } {
  const schemaLookup = buildContextSchemaLookup(options.contextDataSources);
  if (schemaLookup.size === 0 || nodes.length === 0) {
    return { nodes, edges: inputEdges };
  }

  const nextNodes = [...nodes];
  let nextEdges = [...inputEdges];
  const nodesById = new Map(nextNodes.map((node) => [node.id, node]));
  const nodeIds = new Set(nextNodes.map((node) => node.id));
  const singleSchemaEntry = schemaLookup.size === 1 ? [...schemaLookup.values()][0] : null;

  const buildUniqueNodeId = (baseId: string): string => {
    let candidate = baseId;
    let suffix = 1;
    while (nodeIds.has(candidate)) {
      candidate = `${baseId}-${suffix}`;
      suffix += 1;
    }
    nodeIds.add(candidate);
    return candidate;
  };

  let edgePairSet = new Set<string>();
  let edgeIdSet = new Set<string>();

  const rebuildEdgeIndexes = (): void => {
    edgePairSet = new Set(nextEdges.map((edge) => buildEdgeKey(edge.source, edge.target)));
    edgeIdSet = new Set(nextEdges.map((edge) => edge.id));
  };
  rebuildEdgeIndexes();

  const addEdge = (source: string, target: string, suffix: string): void => {
    if (!source || !target || source === target) return;
    if (!nodesById.has(source) || !nodesById.has(target)) return;

    const pairKey = buildEdgeKey(source, target);
    if (edgePairSet.has(pairKey)) return;

    let id = `e-${source}-${target}-${suffix}`;
    let index = 1;
    while (edgeIdSet.has(id)) {
      id = `e-${source}-${target}-${suffix}-${index}`;
      index += 1;
    }

    nextEdges.push({ id, source, target });
    edgePairSet.add(pairKey);
    edgeIdSet.add(id);
  };

  const resolveSchemaForSource = (sourceNode: PipelineNode): Record<string, string> | null => {
    const sourceConfig = toRecordLoose(sourceNode.data.config);
    const sourceId = pickString(sourceConfig.dataSourceId, options.defaultSourceId);
    if (sourceId && schemaLookup.has(sourceId)) {
      return schemaLookup.get(sourceId) || null;
    }
    return singleSchemaEntry;
  };

  const sourceNodes = sortNodesForFlow(nextNodes).filter((node) => node.data.type === 'SOURCE');

  sourceNodes.forEach((sourceNode) => {
    const schema = resolveSchemaForSource(sourceNode);
    if (!schema) return;

    const castRules = buildCastRulesFromSchema(schema);
    if (castRules.length === 0) return;

    const outgoingEdges = nextEdges.filter((edge) => edge.source === sourceNode.id);
    if (
      outgoingEdges.length === 1 &&
      nodesById.get(outgoingEdges[0].target)?.data.type === 'TRANSFORMATION'
    ) {
      const targetNodeId = outgoingEdges[0].target;
      const targetNode = nodesById.get(targetNodeId);
      if (targetNode) {
        const merged = mergeCastRulesIntoTransformationNode(targetNode, castRules);
        if (merged.added > 0) {
          const targetIndex = nextNodes.findIndex((node) => node.id === targetNodeId);
          if (targetIndex >= 0) {
            nextNodes[targetIndex] = merged.node;
            nodesById.set(targetNodeId, merged.node);
          }
        }
        return;
      }
    }

    const normalizeNodeId = buildUniqueNodeId(`${sourceNode.id}-normalize-types`);
    const outgoingTargets = outgoingEdges.map((edge) => edge.target);
    const targetPositions = outgoingTargets
      .map((targetId) => nodesById.get(targetId))
      .filter((node): node is PipelineNode => !!node)
      .map((node) => node.position.x);
    const sourceX = sourceNode.position?.x ?? 0;
    const sourceY = sourceNode.position?.y ?? 120;
    const nearestTargetX =
      targetPositions.length > 0 ? Math.min(...targetPositions) : sourceX + 260;
    const normalizeX =
      nearestTargetX > sourceX
        ? sourceX + Math.max(120, Math.floor((nearestTargetX - sourceX) / 2))
        : sourceX + 160;

    const normalizeNode: PipelineNode = {
      id: normalizeNodeId,
      type: 'TRANSFORMATION',
      position: { x: normalizeX, y: sourceY },
      data: {
        label: 'Normalize Data Types',
        type: 'TRANSFORMATION',
        config: {
          type: 'TRANSFORMATION',
          transformations: castRules,
        },
      },
    };

    nextNodes.push(normalizeNode);
    nodesById.set(normalizeNodeId, normalizeNode);

    if (outgoingTargets.length > 0) {
      nextEdges = nextEdges.filter((edge) => edge.source !== sourceNode.id);
      rebuildEdgeIndexes();

      addEdge(sourceNode.id, normalizeNodeId, 'normalize');
      outgoingTargets.forEach((targetId) => {
        addEdge(normalizeNodeId, targetId, 'normalize');
      });
    } else {
      addEdge(sourceNode.id, normalizeNodeId, 'normalize');
      const fallbackTarget = sortNodesForFlow(nextNodes).find(
        (node) =>
          node.id !== sourceNode.id &&
          node.id !== normalizeNodeId &&
          node.data.type !== 'SOURCE',
      );
      if (fallbackTarget) {
        addEdge(normalizeNodeId, fallbackTarget.id, 'normalize');
      }
    }
  });

  return {
    nodes: nextNodes,
    edges: nextEdges,
  };
}

function parseRecommendedNodeSpecs(option?: PipelineOption | null): RecommendedNodeSpec[] {
  if (!option || !Array.isArray(option.nodes)) return [];

  return option.nodes
    .map((nodeSummary) => {
      const summaryRecord = toRecordLoose(nodeSummary);
      const rawType = pickString(summaryRecord.type);
      if (!rawType) return null;
      return {
        rawType: rawType.toUpperCase(),
        type: normalizeNodeType(rawType),
        label: pickString(summaryRecord.label) || rawType,
      };
    })
    .filter((item): item is RecommendedNodeSpec => !!item);
}

function buildDefaultConfigForNodeType(
  type: PipelineNodeType,
  options: {
    rawType?: string;
    defaultSourceId: string;
    index: number;
  },
): PipelineNodeData['config'] {
  if (type === 'SOURCE') {
    return {
      type: 'SOURCE',
      dataSourceId: options.defaultSourceId,
    };
  }

  if (type === 'DESTINATION') {
    return {
      type: 'DESTINATION',
      targetTable: `ai_pipeline_output_${Date.now()}_${options.index + 1}`,
      writeMode: 'UPSERT',
    };
  }

  if (type === 'FILTER') {
    return {
      type: 'FILTER',
      conditions: [],
      operator: 'AND',
    };
  }

  if (type === 'AGGREGATION') {
    return {
      type: 'AGGREGATION',
      groupBy: [],
      aggregations: [],
    };
  }

  if (type === 'JOIN') {
    return {
      type: 'JOIN',
      joinType: 'INNER',
      leftKey: '',
      rightKey: '',
    };
  }

  if (type === 'UNION') {
    return {
      type: 'UNION',
      unionMode: 'ALL',
    };
  }

  // TRANSFORMATION
  const isProjection = toString(options.rawType).toUpperCase() === 'PROJECTION';
  return {
    type: 'TRANSFORMATION',
    transformations: isProjection
      ? [
          {
            id: `transform-projection-auto-${options.index + 1}`,
            type: 'CUSTOM',
            config: {
              operation: 'PROJECTION',
              timeField: '',
              metricField: '',
              groupBy: [],
              projectionYears: 1,
              method: 'linear',
            },
          },
        ]
      : [],
  };
}

function buildRecommendedNode(
  spec: RecommendedNodeSpec,
  existingIds: Set<string>,
  index: number,
  defaultSourceId: string,
): PipelineNode {
  let id = `${spec.type.toLowerCase()}-rec-${index + 1}`;
  let suffix = 1;
  while (existingIds.has(id)) {
    id = `${spec.type.toLowerCase()}-rec-${index + 1}-${suffix}`;
    suffix += 1;
  }
  existingIds.add(id);

  return {
    id,
    type: spec.type,
    position: { x: 80 + index * 220, y: 120 },
    data: {
      label: spec.label || `${spec.type} ${index + 1}`,
      type: spec.type,
      config: buildDefaultConfigForNodeType(spec.type, {
        rawType: spec.rawType,
        defaultSourceId,
        index,
      }),
    },
  };
}

function alignNodesWithRecommendation(
  nodes: PipelineNode[],
  selectedOption: PipelineOption | null | undefined,
  defaultSourceId: string,
): PipelineNode[] {
  const specs = parseRecommendedNodeSpecs(selectedOption);
  if (specs.length === 0 || nodes.length === 0) return nodes;

  const sortedByPosition = sortNodesForFlow(nodes);
  const existingIds = new Set(sortedByPosition.map((node) => node.id));
  const usedIds = new Set<string>();
  const aligned: PipelineNode[] = [];

  specs.forEach((spec, index) => {
    const matched = sortedByPosition.find(
      (node) => node.data.type === spec.type && !usedIds.has(node.id),
    );

    if (matched) {
      usedIds.add(matched.id);
      aligned.push({
        ...matched,
        data: {
          ...matched.data,
          label: spec.label || matched.data.label,
        },
      });
      return;
    }

    aligned.push(buildRecommendedNode(spec, existingIds, index, defaultSourceId));
  });

  sortedByPosition.forEach((node) => {
    if (!usedIds.has(node.id)) {
      aligned.push(node);
      usedIds.add(node.id);
    }
  });

  return aligned.map((node, index) => ({
    ...node,
    position: {
      x: 80 + index * 220,
      y: typeof node.position?.y === 'number' ? node.position.y : 120,
    },
  }));
}

function normalizePosition(
  rawPosition: unknown,
  index: number,
): { x: number; y: number } {
  const position = toRecordLoose(rawPosition);
  const x = pickNumber(position.x, position.left);
  const y = pickNumber(position.y, position.top);
  return {
    x: typeof x === 'number' ? x : 80 + index * 260,
    y: typeof y === 'number' ? y : 120,
  };
}

function normalizeConfig(
  type: PipelineNodeType,
  rawNodeType: unknown,
  rawConfig: unknown,
  defaultSourceId: string,
  index: number,
): PipelineNodeData['config'] {
  const config = toRecordLoose(rawConfig);

  if (type === 'SOURCE') {
    const sourceObj = toRecordLoose(config.dataSource || config.source);
    const dataSourceId =
      pickString(
        config.dataSourceId,
        config.datasourceId,
        config.data_source_id,
        config.sourceId,
        sourceObj.id,
      ) || defaultSourceId;
    return {
      type: 'SOURCE',
      dataSourceId,
      query: pickString(
        config.query,
        config.customQuery,
        config.sql,
        config.sqlQuery,
        config.custom_query,
        config.statement,
        config.rawQuery,
      ) || undefined,
      limit: pickNumber(config.limit, config.maxRows, config.rowLimit, config.take),
    };
  }

  if (type === 'DESTINATION') {
    const targetTable =
      pickString(
        config.targetTable,
        config.tableName,
        config.table,
        config.destinationTable,
        config.outputTable,
        config.sinkTable,
        config.dataset,
      ) || `ai_pipeline_output_${Date.now()}_${index + 1}`;
    return {
      type: 'DESTINATION',
      targetTable,
      writeMode: normalizeWriteMode(config.writeMode || config.mode || config.saveMode),
      upsertKey: pickStringArray(
        config.upsertKey,
        config.keyFields,
        config.keys,
        config.primaryKey,
        config.mergeKeys,
      ),
      isPublic: pickBoolean(config.isPublic, config.public, config.publish),
      expiresAt: pickString(config.expiresAt, config.expiration, config.expiresOn) || undefined,
    };
  }

  if (type === 'FILTER') {
    const normalized = normalizeFilterConditions(config);
    const explicitOperator = pickString(config.operator, config.logicalOperator).toUpperCase();
    return {
      type: 'FILTER',
      conditions: normalized.conditions,
      operator:
        explicitOperator === 'OR'
          ? 'OR'
          : explicitOperator === 'AND'
            ? 'AND'
            : normalized.detectedLogicalOperator || 'AND',
    };
  }

  if (type === 'AGGREGATION') {
    return {
      type: 'AGGREGATION',
      groupBy: pickStringArray(
        config.groupBy,
        config.group_by,
        config.dimensions,
        config.keys,
        config.by,
      ),
      aggregations: normalizeAggregationRules(config),
    };
  }

  if (type === 'JOIN') {
    const joinType = pickString(config.joinType, config.type, config.method).toUpperCase();
    const on = toRecordLoose(config.on || config.keys);
    const parsedOn = parseConditionExpression(pickString(config.onExpression, config.onClause));
    const key = pickString(config.key, config.joinKey);
    return {
      type: 'JOIN',
      joinType: joinType === 'LEFT' || joinType === 'RIGHT' || joinType === 'FULL' ? joinType : 'INNER',
      leftKey: pickString(
        config.leftKey,
        config.leftField,
        config.left,
        on.left,
        parsedOn?.field,
        key,
      ),
      rightKey: pickString(
        config.rightKey,
        config.rightField,
        config.right,
        on.right,
        parsedOn ? String(parsedOn.value) : '',
        key,
      ),
    };
  }

  if (type === 'UNION') {
    const deduplicate = pickBoolean(config.deduplicate, config.distinct);
    const mode = pickString(config.unionMode, config.mode).toUpperCase();
    return {
      type: 'UNION',
      unionMode:
        mode === 'DISTINCT' || deduplicate === true
          ? 'DISTINCT'
          : 'ALL',
    };
  }

  return {
    type: 'TRANSFORMATION',
    transformations: normalizeTransformations(config, rawNodeType),
  };
}

function ensureSourceDestination(
  nodes: PipelineNode[],
  defaultSourceId: string,
): PipelineNode[] {
  const hasSource = nodes.some((node) => node.data.type === 'SOURCE');
  const hasDestination = nodes.some((node) => node.data.type === 'DESTINATION');
  const nextNodes = [...nodes];

  if (!hasSource) {
    nextNodes.unshift({
      id: 'source-auto',
      type: 'SOURCE',
      position: { x: 80, y: 120 },
      data: {
        label: 'Source',
        type: 'SOURCE',
        config: {
          type: 'SOURCE',
          dataSourceId: defaultSourceId,
        },
      },
    });
  }

  if (!hasDestination) {
    nextNodes.push({
      id: 'destination-auto',
      type: 'DESTINATION',
      position: { x: 620, y: 120 },
      data: {
        label: 'Destination',
        type: 'DESTINATION',
        config: {
          type: 'DESTINATION',
          targetTable: `ai_pipeline_output_${Date.now()}`,
          writeMode: 'UPSERT',
        },
      },
    });
  }

  return nextNodes;
}

function normalizeEdges(
  rawEdges: AIPipelineEdge[] | unknown,
  nodeIds: Set<string>,
): PipelineEdge[] {
  const safeEdges: PipelineEdge[] = [];
  const seen = new Set<string>();
  const edges = Array.isArray(rawEdges) ? rawEdges : [];

  edges.forEach((rawEdge, index) => {
    const edge = toRecordLoose(rawEdge);
    const source = pickString(edge.source, edge.sourceId, edge.from);
    const target = pickString(edge.target, edge.targetId, edge.to);
    if (!source || !target) return;
    if (!nodeIds.has(source) || !nodeIds.has(target)) return;

    const rawId = pickString(edge.id);
    let id = rawId || `e-${source}-${target}-${index + 1}`;
    if (seen.has(id)) {
      id = `${id}-${index + 1}`;
    }
    seen.add(id);
    safeEdges.push({ id, source, target });
  });

  return safeEdges;
}

function buildSequentialEdges(nodes: PipelineNode[]): PipelineEdge[] {
  const edges: PipelineEdge[] = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    edges.push({
      id: `e-${nodes[i].id}-${nodes[i + 1].id}-${i + 1}`,
      source: nodes[i].id,
      target: nodes[i + 1].id,
    });
  }
  return edges;
}

function buildEdgeKey(source: string, target: string): string {
  return `${source}::${target}`;
}

function sortNodesForFlow(nodes: PipelineNode[]): PipelineNode[] {
  return [...nodes].sort((a, b) => {
    if (a.position.x !== b.position.x) return a.position.x - b.position.x;
    if (a.position.y !== b.position.y) return a.position.y - b.position.y;
    return a.id.localeCompare(b.id);
  });
}

function ensureConnectedEdges(
  nodes: PipelineNode[],
  inputEdges: PipelineEdge[],
): PipelineEdge[] {
  if (nodes.length <= 1) return inputEdges;

  const orderedNodes = sortNodesForFlow(nodes);
  const nodeIds = new Set(orderedNodes.map((node) => node.id));
  const pairSeen = new Set<string>();
  const idSeen = new Set<string>();
  const edges: PipelineEdge[] = [];

  inputEdges.forEach((edge, index) => {
    if (!nodeIds.has(edge.source) || !nodeIds.has(edge.target)) return;
    if (edge.source === edge.target) return;
    const pairKey = buildEdgeKey(edge.source, edge.target);
    if (pairSeen.has(pairKey)) return;

    let id = pickString(edge.id) || `e-${edge.source}-${edge.target}-${index + 1}`;
    if (idSeen.has(id)) {
      id = `${id}-${index + 1}`;
    }

    pairSeen.add(pairKey);
    idSeen.add(id);
    edges.push({ id, source: edge.source, target: edge.target });
  });

  const addEdge = (source: string, target: string): void => {
    if (!source || !target || source === target) return;
    if (!nodeIds.has(source) || !nodeIds.has(target)) return;
    const pairKey = buildEdgeKey(source, target);
    if (pairSeen.has(pairKey)) return;
    const id = `e-${source}-${target}-auto-${edges.length + 1}`;
    pairSeen.add(pairKey);
    idSeen.add(id);
    edges.push({ id, source, target });
  };

  const buildDegreeMaps = (): { indegree: Map<string, number>; outdegree: Map<string, number> } => {
    const indegree = new Map<string, number>();
    const outdegree = new Map<string, number>();
    orderedNodes.forEach((node) => {
      indegree.set(node.id, 0);
      outdegree.set(node.id, 0);
    });
    edges.forEach((edge) => {
      outdegree.set(edge.source, (outdegree.get(edge.source) || 0) + 1);
      indegree.set(edge.target, (indegree.get(edge.target) || 0) + 1);
    });
    return { indegree, outdegree };
  };

  const sources = orderedNodes.filter((node) => node.data.type === 'SOURCE');
  const destinations = orderedNodes.filter((node) => node.data.type === 'DESTINATION');
  const middle = orderedNodes.filter(
    (node) => node.data.type !== 'SOURCE' && node.data.type !== 'DESTINATION',
  );
  const primarySource = sources[0] || orderedNodes[0];
  const primaryDestination = destinations[destinations.length - 1] || orderedNodes[orderedNodes.length - 1];

  const chainNodes: PipelineNode[] = [];
  if (primarySource) chainNodes.push(primarySource);
  middle.forEach((node) => {
    if (!chainNodes.some((item) => item.id === node.id)) {
      chainNodes.push(node);
    }
  });
  if (primaryDestination && !chainNodes.some((item) => item.id === primaryDestination.id)) {
    chainNodes.push(primaryDestination);
  }

  if (chainNodes.length < 2) {
    orderedNodes.forEach((node) => {
      if (!chainNodes.some((item) => item.id === node.id)) {
        chainNodes.push(node);
      }
    });
  }

  for (let i = 0; i < chainNodes.length - 1; i++) {
    addEdge(chainNodes[i].id, chainNodes[i + 1].id);
  }

  const firstDownstream =
    chainNodes.find((node) => node.id !== primarySource?.id && node.data.type !== 'SOURCE') ||
    chainNodes[1];
  if (firstDownstream) {
    sources.forEach((source) => {
      if (source.id === primarySource?.id) return;
      addEdge(source.id, firstDownstream.id);
    });
  }

  const lastUpstream =
    [...chainNodes]
      .reverse()
      .find((node) => node.data.type !== 'DESTINATION' && node.id !== primaryDestination?.id) ||
    chainNodes[chainNodes.length - 2];
  if (lastUpstream) {
    destinations.forEach((destination) => {
      if (destination.id === primaryDestination?.id) return;
      addEdge(lastUpstream.id, destination.id);
    });
  }

  const indexById = new Map<string, number>(
    orderedNodes.map((node, index) => [node.id, index]),
  );
  let { indegree, outdegree } = buildDegreeMaps();

  orderedNodes.forEach((node) => {
    const nodeIndex = indexById.get(node.id);
    if (nodeIndex === undefined) return;

    if (node.data.type !== 'SOURCE' && (indegree.get(node.id) || 0) === 0) {
      for (let i = nodeIndex - 1; i >= 0; i--) {
        const candidate = orderedNodes[i];
        if (candidate.data.type === 'DESTINATION') continue;
        addEdge(candidate.id, node.id);
        ({ indegree, outdegree } = buildDegreeMaps());
        break;
      }
    }

    if (node.data.type !== 'DESTINATION' && (outdegree.get(node.id) || 0) === 0) {
      for (let i = nodeIndex + 1; i < orderedNodes.length; i++) {
        const candidate = orderedNodes[i];
        if (candidate.data.type === 'SOURCE') continue;
        addEdge(node.id, candidate.id);
        ({ indegree, outdegree } = buildDegreeMaps());
        break;
      }
    }
  });

  const ensureMultiInputNode = (nodeType: PipelineNodeType, minimumIncoming: number): void => {
    orderedNodes
      .filter((node) => node.data.type === nodeType)
      .forEach((node) => {
        const nodeIndex = indexById.get(node.id);
        if (nodeIndex === undefined) return;

        const incomingSources = new Set(
          edges.filter((edge) => edge.target === node.id).map((edge) => edge.source),
        );
        if (incomingSources.size >= minimumIncoming) return;

        const upstreamCandidates = orderedNodes
          .slice(0, nodeIndex)
          .filter((candidate) => candidate.id !== node.id && candidate.data.type !== 'DESTINATION');

        for (let i = upstreamCandidates.length - 1; i >= 0; i--) {
          if (incomingSources.size >= minimumIncoming) break;
          const candidate = upstreamCandidates[i];
          if (incomingSources.has(candidate.id)) continue;
          addEdge(candidate.id, node.id);
          incomingSources.add(candidate.id);
        }
      });
  };

  ensureMultiInputNode('JOIN', 2);
  ensureMultiInputNode('UNION', 2);

  return edges;
}

function extractNodeId(node: Record<string, unknown>, fallback: string): string {
  return pickString(node.id, node.nodeId, node.key) || fallback;
}

function extractNodeType(node: Record<string, unknown>): unknown {
  const nodeData = toRecordLoose(node.data);
  return node.type || nodeData.type || node.nodeType;
}

function extractNodeLabel(node: Record<string, unknown>, fallback: string): string {
  const nodeData = toRecordLoose(node.data);
  return pickString(node.label, node.name, node.title, nodeData.label, nodeData.name) || fallback;
}

function extractNodePosition(node: Record<string, unknown>): unknown {
  const nodeData = toRecordLoose(node.data);
  return node.position || nodeData.position;
}

function extractNodeConfig(node: Record<string, unknown>): Record<string, unknown> {
  const nodeData = toRecordLoose(node.data);
  return mergeRecordCandidates(
    nodeData.config,
    nodeData.params,
    nodeData.settings,
    node.configuration,
    node.settings,
    node.params,
    node.config,
  );
}

function unwrapPipelinePayload(rawPipeline: GeneratedAIPipeline): Record<string, unknown> {
  let current: unknown = rawPipeline;
  const seen = new Set<unknown>();

  for (let depth = 0; depth < 4; depth++) {
    const record = toRecordLoose(current);
    if (Object.keys(record).length === 0 || seen.has(record)) {
      break;
    }
    seen.add(record);

    if (Array.isArray(record.nodes) || Array.isArray(record.edges)) {
      return record;
    }

    const next =
      record.pipeline ||
      record.pipelineConfig ||
      record.data ||
      record.result;

    if (!next) {
      return record;
    }

    current = next;
  }

  return toRecordLoose(current);
}

export interface NormalizedGeneratedPipeline {
  nodes: PipelineNode[];
  edges: PipelineEdge[];
  explanation: string;
  metadata: AIPipelineMetadata;
}

export function normalizeGeneratedPipeline(
  rawPipeline: GeneratedAIPipeline,
  selectedDataSourceIds: string[] = [],
  options: NormalizeGeneratedPipelineOptions = {},
): NormalizedGeneratedPipeline {
  const payload = unwrapPipelinePayload(rawPipeline);
  const rawMetadata = toRecordLoose(payload.metadata);
  const metadataConfidence = pickNumber(rawMetadata.confidence);
  const rawNodes = Array.isArray(payload.nodes) ? (payload.nodes as AIPipelineNode[]) : [];
  const defaultSourceId = selectedDataSourceIds[0] || '';
  const seenNodeIds = new Set<string>();

  let nodes: PipelineNode[] = rawNodes
    .map((rawNode, index) => {
      const node = toRecordLoose(rawNode);
      const rawNodeType = extractNodeType(node);
      const nodeType = normalizeNodeType(rawNodeType);
      const fallbackId = `${nodeType.toLowerCase()}-${index + 1}`;
      const rawId = extractNodeId(node, fallbackId);
      const id = seenNodeIds.has(rawId) ? `${rawId}-${index + 1}` : rawId;
      seenNodeIds.add(id);

      return {
        id,
        type: nodeType,
        position: normalizePosition(extractNodePosition(node), index),
        data: {
          label: extractNodeLabel(node, `${nodeType} ${index + 1}`),
          type: nodeType,
          config: normalizeConfig(nodeType, rawNodeType, extractNodeConfig(node), defaultSourceId, index),
        },
      };
    })
    .filter((node) => !!node.id);

  nodes = alignNodesWithRecommendation(nodes, options.selectedOption, defaultSourceId);
  nodes = ensureSourceDestination(nodes, defaultSourceId);

  const nodeIds = new Set(nodes.map((node) => node.id));
  let edges = normalizeEdges(payload.edges, nodeIds);
  if (edges.length === 0 && nodes.length > 1) {
    edges = buildSequentialEdges(nodes);
  }
  const normalizedForTypeLayer = ensureTypeNormalizationLayer(nodes, edges, {
    contextDataSources: options.contextDataSources,
    defaultSourceId,
  });
  nodes = normalizedForTypeLayer.nodes;
  edges = normalizedForTypeLayer.edges;
  edges = ensureConnectedEdges(nodes, edges);

  return {
    nodes,
    edges,
    explanation:
      typeof payload.explanation === 'string' && payload.explanation.trim().length > 0
        ? payload.explanation
        : 'Pipeline generated and normalized from AI response.',
    metadata: {
      generatedBy: pickString(rawMetadata.generatedBy) || 'ai',
      confidence:
        typeof metadataConfidence === 'number'
          ? Math.max(0, Math.min(1, metadataConfidence))
          : 0.8,
    },
  };
}
